<template>
    <header-index />

    <!-- 行业横幅 -->
    <section class="category-banner flex" style="background-image: url('https://images.leaflypackaging.com/profile/upload/2025/11/24/industry_banner_20251124100330A061.webp');">
        <div class="container mx-auto px-4 content-center">
            <h1 class="leafly-page-title text-center mb-8">Custom packaging solutions for All Industries</h1>
            <p class="leafly-page-second-title text-center max-w-4xl mx-auto">We has come to know the ins and outs of most industries and has extensive experience providing packaging solutions to 5000+ businesses worldwide. We provide customized solutions to meet the needs of a wide range of industries. From design to delivery, we ensure your product stands out with unmatched quality and precision, no matter what industry you're from.</p>
        </div>
    </section>

    <!-- 行业图标网格 -->
    <section class="py-16 bg-gray-100">
        <div class="container mx-auto px-4">
             <!-- 行业列表 -->
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <nuxt-link 
                    v-for="(industry, index) in industryList" 
                    :key="index"
                    :to="industry.url" 
                    class="industry-item"
                    :class="{'industry-item-active': industry.url }">
                    <div class="w-16 h-16 mb-4">
                        <img :src="`/leaflypackaging-com/images/industry/${industry.imageUrl}`" :alt="industry.name" class="industry-icon w-full h-full object-contain" />
                    </div>
                    <p  class="text-center text-sm font-weight-400  transition-all">{{ industry.name }}</p>
                </nuxt-link>
            </div>

            <!-- 找不到您的行业？ -->
            <div class="mt-12 bg-green-600 rounded-lg p-6 text-white">
                <div class="flex flex-col md:flex-row items-center justify-between">
                    <div>
                        <h3 class="text-xl font-weight-600 mb-2">Can't find your industry on our list?</h3>
                        <p class="text-sm">Contact our packaging experts to see how we can help.</p>
                    </div>
                    <a href="#" class="mt-[-12px] md:mt-0 bg-yellow-400 text-gray-800 px-6 py-2 rounded-full font-weight-400 hover:bg-yellow-500 transition-colors">Ask for help</a>
                </div>
            </div>
        </div>
    </section>

    <!-- 材料说明 -->
    <section class="py-16">
        <div class="container mx-auto px-4">
            <h2 class="text-2xl md:text-3xl font-weight-600 text-center mb-6">Materials</h2>
            <!-- <p class="text-center text-gray-600 mb-10 max-w-3xl mx-auto">Explore the endless creative possibilities at Leaflypackaging. Whether you're looking to elevate your product presentation or create a memorable unboxing experience, we'll be your partner to make it happen.</p> -->

            <!-- 材料展示网格 -->
            <div class="materials-grid">
                <div 
                    v-for="(material, index) in materialsList" 
                    :key="index"
                    class="material-item"
                >
                    <div class="material-image-wrapper">
                        <img 
                            :src="material.image" 
                            :alt="material.name" 
                            class="material-image"
                        />
                        <div class="material-overlay">
                            <div class="material-description">
                                {{ material.description }}
                            </div>
                        </div>
                    </div>
                    <div class="material-title">
                        {{ material.name }}
                    </div>
                </div>
            </div>
        </div>
    </section>

    <sectionCTA />
    <footer-index />
</template>

<script setup lang="ts" name="SinglePageIndustry">
    import type { SinglePage } from '~/types';
    import cmsConfig  from '~/cmsConfig'
    import cmsUtils from '~/utils/cmsUtils';
    import headerIndex from '../../components/header/header-index.vue'
    import footerIndex from '../../components/footer/footer-index.vue'
    import sectionCTA from '../../components/common/sectionCTA.vue'
    const props = defineProps<{
      singlePage: SinglePage
    }>()

    // 行业列表数据
    const industryList = [
        { url: '/tags/cannabis', imageUrl: 'cannabis.svg', name: 'Cannabis' },
        { url: '', imageUrl: 'cosmetics.svg', name: 'Cosmetics' },
        { url: '', imageUrl: 'perfume.svg', name: 'Perfume' },
        { url: '', imageUrl: 'candle.svg', name: 'Candle' },
        { url: '', imageUrl: 'gift.svg', name: 'Gift' },
        { url: '', imageUrl: 'jewelry.svg', name: 'Jewelry' },
        { url: '', imageUrl: 'pet.svg', name: 'Pet' },
        { url: '', imageUrl: 'apparel.svg', name: 'Apparel' },
        { url: '', imageUrl: 'restaurant.svg', name: 'Restaurant' },
        { url: '', imageUrl: 'food.svg', name: 'Food' },
        { url: '', imageUrl: 'pharmacy.svg', name: 'Pharmacy' },
        { url: '', imageUrl: 'beer-liquor.svg', name: 'Beer & Liquor' },
        { url: '', imageUrl: 'electronics.svg', name: 'Electronics' },
        { url: '', imageUrl: 'toys.svg', name: 'Toys' },
        { url: '', imageUrl: 'candy.svg', name: 'Candy' },
        { url: '', imageUrl: 'health.svg', name: 'Health' },
        { url: '', imageUrl: 'beverage.svg', name: 'Beverage' },
        { url: '', imageUrl: 'vape.svg', name: 'Vape' }
    ]

    // 材料列表数据
    const materialsList = [
        {
            name: 'Paperboard',
            image: 'https://images.leaflypackaging.com/profile/upload/2025/12/02/3_20251202154815A079.webp',
            description: 'With expertise in complex designs, specialist inks, finishes, and captivating embellishments, we create aesthetically inspiring packaging designs.'
        },
        {
            name: 'Rigid board',
            image: 'https://images.leaflypackaging.com/profile/upload/2025/12/02/6_20251202154816A083.webp',
            description: 'From basic lid and base set top boxes to intricately wrapped hinged structures adorned with stylish trimmings, our material design is a testament to our innovative nature. We breathe life into every creation, channelling creativity, and meticulously blending form and function.'
        },
        {
            name: 'Pulp',
            image: 'https://images.leaflypackaging.com/profile/upload/2025/12/30/3_20251230182304A058.jpg',
            description: 'We push the boundaries of molded pulp, mastering artisanal techniques that ensure quality, protection, and endless decorative capabilities. From embossing and debossing to printing and foiling, we offer a range of techniques that elevate your packaging to new heights.'
        },
        {
            name: 'Material/Leather',
            image: 'https://images.leaflypackaging.com/profile/upload/2025/12/02/5_20251202154816A080.webp',
            description: 'Decades of refining the art of material craftsmanship have fueled our expertise. From natural hides to embroidered silk, we ensure your packaging is uniquely exceptional.'
        },
        {
            name: 'Flexible/Rigid Plastic',
            image: 'https://images.leaflypackaging.com/profile/upload/2025/12/02/7_20251202154816A084.webp',
            description: 'We have mastered every plastic process over the years, from complex injection molding and extrusion to thermoforming fitments and PET folding boxes.'
        },
        {
            name: 'Wood',
            image: 'https://images.leaflypackaging.com/profile/upload/2025/12/30/6_20251230182304A059.jpg',
            description: 'Our reverence for wood, the oldest known material, is evident in every design. From solid to composite, including mahogany, maple, walnut, and wenge, our craftsmen blend artistry and practicality to create masterful wood-based results.'
        },
        {
            name: 'Tin/Metal',
            image: 'https://images.leaflypackaging.com/profile/upload/2025/12/02/2_20251202154816A082.webp',
            description: 'Sturdy, enduring, and recyclable, tin and metal have long been cornerstones of the packaging industry. Whether molded, stamped, welded, bent, or cast, the possibilities for creating remarkable packaging formats are endless.'
        },
        {
            name: 'Glass',
            image: 'https://images.leaflypackaging.com/profile/upload/2025/12/02/8_20251202154816A085.webp',
            description: 'Glass is a stable and sustainable packaging material, virtually impermeable and infinitely recyclable. Our expertise in working with glass has resulted in beautiful, bespoke designs.'
        },
        {
            name: 'Other Materials',
            image: 'https://images.leaflypackaging.com/profile/upload/2025/12/02/9_20251202154817A086.webp',
            description: 'LeaflyPackaging is an intrepid explorer, defying norms with unique packaging crafted from unconventional materials. From blends to truly unique substances, we create truly exceptional designs that reflect your brand\'s distinctiveness.'
        }
    ]

    const cmsInfo = await cmsUtils.getCmsInfo()
    const route = useRoute()
    const baseUrl = (cmsInfo.website.https ? 'https://' : 'http://') + cmsInfo.website.domain
    const canonicalUrl = baseUrl + route.path.replace('.html','')
    useHead({
        script: [
            {
                type: 'application/ld+json',
                innerHTML: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'WebPage',
                    name: props.singlePage.title,
                    description: props.singlePage.description,
                    url: canonicalUrl,
                    about: {
                        '@type': 'ItemList',
                        name: 'Industries Served',
                        itemListElement: industryList.map((item, index) => ({
                            '@type': 'ListItem',
                            position: index + 1,
                            item: {
                                '@type': 'Service',
                                name: item.name + ' Packaging'
                            }
                        }))
                    },
                    keywords: industryList.map(item => item.name).join(', ')
                })
            }
        ]
    })


</script>

<style scoped lang="scss">
    .industry-item {
        background-color: white;
        padding: 1.5rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: 0.5rem;
        box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
        transition-property: box-shadow;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;

        &:hover {
            box-shadow: 0 4px 6px -1px rgba(18, 112, 1, 0.1), 0 2px 4px -2px rgba(0, 106, 48, 0.1);
        }
        
        p {
            transition: font-weight 0.2s ease, color 0.2s ease;
        }
    }

    .industry-item.industry-item-active {
        &:hover {
            .industry-icon {
                filter: brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(106deg) brightness(104%) contrast(97%);
                transition: filter 0.2s ease;
            }
            p {
                font-weight: bold;
                color: #16a34a;
            }
        }
    }

    // 材料网格样式
    .materials-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 2rem;
        
        @media (min-width: 768px) {
            grid-template-columns: repeat(2, 1fr);
        }
        
        @media (min-width: 1024px) {
            grid-template-columns: repeat(3, 1fr);
        }
    }

    .material-item {
        display: flex;
        flex-direction: column;
    }

    .material-image-wrapper {
        position: relative;
        width: 100%;
        aspect-ratio: 1.53 / 1;
        overflow: hidden;
        border-radius: 0.5rem;
        background-color: #f3f4f6;
        margin-bottom: 1rem;
        
        .material-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.3s ease;
        }
        
        .material-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 91, 8, 0.85);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 2rem;
            opacity: 0;
            transition: opacity 0.3s ease;
            
            .material-description {
                color: white;
                font-size: 1.2rem;
                line-height: 1.6;
                text-align: center;
            }
        }
        
        &:hover {
            .material-image {
                transform: scale(1.05);
            }
            
            .material-overlay {
                opacity: 1;
            }
        }
    }

    .material-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: #111827;
        text-align: center;
        margin-top: 0.5rem;
    }
</style>
